'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface AICountContextValue {
  questionsUsed: number;
  isPro: boolean;
  loaded: boolean;
  refresh: () => Promise<void>;
}

const AICountContext = createContext<AICountContextValue>({
  questionsUsed: 0,
  isPro: false,
  loaded: false,
  refresh: async () => {},
});

export function useAICount() {
  return useContext(AICountContext);
}

export function AICountProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [questionsUsed, setQuestionsUsed] = useState(0);
  const [isPro, setIsPro] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchCount = useCallback(async () => {
    if (!user) {
      setQuestionsUsed(0);
      setIsPro(false);
      setLoaded(true);
      return;
    }

    try {
      // Fetch plan — use maybeSingle() to avoid 406 when profile row doesn't exist yet
      let { data: profileData } = await supabase
        .from('user_profiles')
        .select('plan')
        .eq('id', user.id)
        .maybeSingle();

      // Profile row may be slow to appear on first sign-in — retry once after 1.5s
      if (!profileData) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const { data: retryData } = await supabase
          .from('user_profiles')
          .select('plan')
          .eq('id', user.id)
          .maybeSingle();
        profileData = retryData;
      }

      const pro = profileData?.plan === 'pro';
      setIsPro(pro);

      if (!pro) {
        // Read directly from ai_question_counts using user_id only
        const { data: countRow } = await supabase
          .from('ai_question_counts')
          .select('count')
          .eq('user_id', user.id)
          .maybeSingle();

        setQuestionsUsed(countRow?.count ?? 0);
      } else {
        setQuestionsUsed(0);
      }
    } catch {
      // keep previous values on error
    } finally {
      setLoaded(true);
    }
  }, [user]);

  // Initial load
  useEffect(() => {
    if (authLoading) return;
    fetchCount();
  }, [user, authLoading, fetchCount]);

  // Real-time subscription to ai_question_counts for this user
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`ai_count_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ai_question_counts',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newCount = (payload.new as { count?: number })?.count;
          if (typeof newCount === 'number') {
            setQuestionsUsed(newCount);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return (
    <AICountContext.Provider value={{ questionsUsed, isPro, loaded, refresh: fetchCount }}>
      {children}
    </AICountContext.Provider>
  );
}
